import { RadioGroup } from "@headlessui/react";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import classNames from "src/helpers/classNames";
import { ValueWithStock } from "src/helpers/inventory-helpers/getOptionValuesWithStock";
import SizesExplanation from "./SizesExplanation";
import { Locale } from "next/dist/compiled/@vercel/og/satori";

type SizeProps = {
  selectedSize: string | null;
  setSelectedSize(color: string): void;
  valuesWithStock: ValueWithStock[];
  stickyFooter: boolean;
  isTeens?: boolean;
};

function getYearsLabel(value: string, t: any, locale: string): string {
  const number = parseInt(value, 10);
  if (isNaN(number)) return value; // If value is not a number, return it as is

  if (locale.includes("ar")) {
    // Arabic logic
    if (number >= 3 && number <= 10) {
      return `${value} ${t("years_plural")}`;
    } else {
      return `${value} ${t("years")}`;
    }
  } else {
    // Default to English logic
    return `${value} ${t("years")}`;
  }
}

function NewSizes({ valuesWithStock, selectedSize, setSelectedSize, stickyFooter, isTeens = false }: SizeProps) {
  const t = useTranslations("ProductOverview");
  const locale = useLocale() as Locale;

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-semibold">{isTeens ? t("choose_age") : t("size")}</h2>

      <RadioGroup value={selectedSize} onChange={setSelectedSize} className="mt-2">
        <RadioGroup.Label className="sr-only" role="radio">
          {" "}
          {t("choose_a_size")}
        </RadioGroup.Label>
        <div className="flex sm:flex-wrap">
          {valuesWithStock.map(({ value, stock }) => (
            <RadioGroup.Option
              key={value}
              value={value}
              className={({ active, checked }) =>
                classNames(
                  stock > 0 ? "cursor-pointer focus:outline-none" : "cursor-default opacity-25",
                  active ? "ring-2 ring-gray-900 ring-offset-2" : "",
                  checked
                    ? "border-transparent bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50",
                  "mb-2 me-2 flex min-h-[2.95rem] flex-1 items-center justify-center rounded-sm border text-sm font-medium uppercase last:me-0 sm:min-w-[2.95rem]"
                )
              }
              disabled={stock === 0}
            >
              <RadioGroup.Label as="span">{isTeens ? getYearsLabel(value, t, locale) : value}</RadioGroup.Label>
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
      {!stickyFooter && <SizesExplanation selectedSize={selectedSize} />}
    </div>
  );
}

export default NewSizes;
